/**
 * Hooks TanStack Query pour les rôles et permissions
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { rolesApi } from './rolesApi';
import type {
  RoleDTO,
  PermissionDTO,
  CreerRoleData,
  ModifierRoleData,
  AssignerPermissionsData,
} from '../../application/dto/dtos';

// ============================================================
// Clés de cache
// ============================================================

export const rolesQueryKeys = {
  all: ['roles'] as const,
  lists: () => [...rolesQueryKeys.all, 'list'] as const,
  detail: (id: string) => [...rolesQueryKeys.all, 'detail', id] as const,
  permissions: ['permissions'] as const,
  rolePermissions: (idRole: string) => [...rolesQueryKeys.all, idRole, 'permissions'] as const,
};

// ============================================================
// Queries (READ)
// ============================================================

/**
 * Hook pour lister tous les rôles
 */
export function useRoles(): UseQueryResult<RoleDTO[], Error> {
  return useQuery({
    queryKey: rolesQueryKeys.lists(),
    queryFn: async () => {
      const response = await rolesApi.listerRoles();
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Erreur lors du chargement des rôles');
      }
      return response.data.roles;
    },
  });
}

/**
 * Hook pour obtenir un rôle par ID
 */
export function useRole(idRole: string): UseQueryResult<RoleDTO, Error> {
  return useQuery({
    queryKey: rolesQueryKeys.detail(idRole),
    queryFn: async () => {
      const response = await rolesApi.obtenirRole(idRole);
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Rôle introuvable');
      }
      return response.data.role;
    },
    enabled: !!idRole,
  });
}

/**
 * Hook pour lister toutes les permissions disponibles
 */
export function usePermissions(): UseQueryResult<PermissionDTO[], Error> {
  return useQuery({
    queryKey: rolesQueryKeys.permissions,
    queryFn: async () => {
      const response = await rolesApi.listerPermissions();
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Erreur lors du chargement des permissions');
      }
      return response.data.permissions;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (permissions changent rarement)
  });
}

// ============================================================
// Mutations (WRITE)
// ============================================================

/**
 * Hook pour créer un rôle
 */
export function useCreerRole(): UseMutationResult<RoleDTO, Error, CreerRoleData> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreerRoleData) => {
      const response = await rolesApi.creerRole(data);
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Erreur lors de la création du rôle');
      }
      return response.data.role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
  });
}

/**
 * Hook pour modifier un rôle
 */
export function useModifierRole(): UseMutationResult<
  RoleDTO,
  Error,
  { idRole: string; data: ModifierRoleData }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idRole, data }: { idRole: string; data: ModifierRoleData }) => {
      const response = await rolesApi.modifierRole(idRole, data);
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Erreur lors de la modification du rôle');
      }
      return response.data.role;
    },
    onSuccess: (role: RoleDTO) => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.detail(role.idRole) });
    },
  });
}

/**
 * Hook pour supprimer un rôle
 */
export function useSupprimerRole(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (idRole: string) => {
      const response = await rolesApi.supprimerRole(idRole);
      if (!response.succes) {
        throw new Error(response.message || 'Erreur lors de la suppression du rôle');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
  });
}

/**
 * Hook pour assigner des permissions à un rôle
 */
export function useAssignerPermissions(): UseMutationResult<
  RoleDTO,
  Error,
  { idRole: string; data: AssignerPermissionsData }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idRole, data }: { idRole: string; data: AssignerPermissionsData }) => {
      const response = await rolesApi.assignerPermissions(idRole, data);
      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Erreur lors de l\'assignation des permissions');
      }
      return response.data.role;
    },
    onSuccess: (role: RoleDTO) => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.detail(role.idRole) });
    },
  });
}
